import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Company} from 'src/app/interfaces/company.interface';
import {PopupService} from '../../../../services/popup.service';

@Component({
  selector: 'app-company-contact',
  templateUrl: './company-contact.component.html',
  styleUrls: ['./company-contact.component.scss']
})
export class CompanyContactComponent {
  @Input() report: any;
  @Input() company: Company;
  @Output() editContactModalRequested = new EventEmitter<{ emails: string[]; phoneNumbers: string[]; contactType: string }>();

  // Display limits
  readonly displayLimit = 2;

  constructor(private popupService: PopupService) {}

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  /**
   * Show all phone numbers in a modal
   */
  showAllPhoneNumbers(): void {
    const phoneNumbers = this.uniquePhoneNumbers;

    // Build HTML content for phone numbers
    let content = '<div class="space-y-3 max-h-[60vh] overflow-y-auto">';

    phoneNumbers.forEach((phoneNumber, index) => {
      content += `
        <div class="flex items-center gap-x-3 p-3 bg-gray-50 dark:bg-secondary-700 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-600 transition-colors duration-200">
          <div class="flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 text-secondary-900 dark:text-white">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <a href="tel:${phoneNumber}" class="text-sm font-medium text-blue-500 dark:text-[#91a7ff] hover:underline break-all">
              ${phoneNumber}
            </a>
          </div>
          <div class="flex-shrink-0">
            <span class="text-xs text-gray-500 dark:text-gray-400">#${index + 1}</span>
          </div>
        </div>
      `;
    });

    content += '</div>';
    content += '<p class="mt-4 text-xs text-gray-500 dark:text-gray-400">Click on any phone number to initiate a call</p>';

    // Show modal using PopupService
    this.popupService.info(`All Phone Numbers (${phoneNumbers.length})`, content);
  }

  /**
   * Show all email addresses in a modal
   */
  showAllEmails(): void {
    const emails = this.uniqueEmails;

    // Build HTML content for emails
    let content = '<div class="space-y-3 max-h-[60vh] overflow-y-auto">';

    emails.forEach((email, index) => {
      content += `
        <div class="flex items-center gap-x-3 p-3 bg-gray-50 dark:bg-secondary-700 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-600 transition-colors duration-200">
          <div class="flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 text-secondary-900 dark:text-white">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <a href="mailto:${email}" class="text-sm font-medium text-blue-500 dark:text-[#91a7ff] hover:underline break-all">
              ${email}
            </a>
          </div>
          <div class="flex-shrink-0">
            <span class="text-xs text-gray-500 dark:text-gray-400">#${index + 1}</span>
          </div>
        </div>
      `;
    });

    content += '</div>';
    content += '<p class="mt-4 text-xs text-gray-500 dark:text-gray-400">Click on any email address to compose a message</p>';

    // Show modal using PopupService
    this.popupService.info(`All Email Addresses (${emails.length})`, content);
  }

  /**
   * Get all unique phone numbers from veridion_data
   */
  get uniquePhoneNumbers(): string[] {
    const phoneNumbers: string[] = [];

    // Get phone numbers from the ACTIVE company's veridion_data (NOT the
    // report-level veridion_data, which is not refreshed on a company switch and
    // would show the previous company's contacts). No fallback: if the switched
    // company has no veridion data, show nothing.
    if (this.company?.veridion_data?.result && this.company.veridion_data.result.length > 0) {
      const veridionPhones = this.company.veridion_data.result[0].phone_numbers;
      if (veridionPhones && Array.isArray(veridionPhones)) {
        phoneNumbers.push(...veridionPhones);
      }
    }

    // Also include the company phone if available
    if (this.company?.phone?.number) {
      phoneNumbers.push(this.company.phone.number);
    }

    // Remove duplicates and filter out null/undefined/empty values
    return [...new Set(phoneNumbers.filter(phone => phone && phone.trim() !== ''))];
  }

  /**
   * Get all unique email addresses from veridion_data
   */
  get uniqueEmails(): string[] {
    const emails: string[] = [];

    // Get emails from the ACTIVE company's veridion_data (NOT the report-level
    // veridion_data, which is not refreshed on a company switch and would show
    // the previous company's contacts). No fallback to report-level data.
    if (this.company?.veridion_data?.result && this.company.veridion_data.result.length > 0) {
      const veridionData = this.company.veridion_data.result[0];

      // Primary email
      if (veridionData.primary_email) {
        emails.push(veridionData.primary_email);
      }

      // General emails field
      if (veridionData.emails) {
        // emails might be a string with comma-separated values
        if (typeof veridionData.emails === 'string') {
          emails.push(...veridionData.emails.split(',').map((e: string) => e.trim()));
        } else if (Array.isArray(veridionData.emails)) {
          emails.push(...veridionData.emails);
        }
      }

      // Other emails
      if (veridionData.other_emails) {
        // other_emails might be a string with comma-separated values
        if (typeof veridionData.other_emails === 'string') {
          emails.push(...veridionData.other_emails.split(',').map((e: string) => e.trim()));
        } else if (Array.isArray(veridionData.other_emails)) {
          emails.push(...veridionData.other_emails);
        }
      }
    }

    // Remove duplicates and filter out null/undefined/empty values
    return [...new Set(emails.filter(email => email && email.trim() !== ''))];
  }

  /**
   * Check if there are any phone numbers to display
   */
  get hasPhoneNumbers(): boolean {
    return this.uniquePhoneNumbers.length > 0;
  }

  /**
   * Check if there are any emails to display
   */
  get hasEmails(): boolean {
    return this.uniqueEmails.length > 0;
  }

  openEditContactModal(): void {
    this.editContactModalRequested.emit({
      emails: this.uniqueEmails,
      phoneNumbers: this.uniquePhoneNumbers,
      contactType: 'company'
    });
  }
}
